/**
 * Utility functions for handling image URLs
 * Prevents double processing by NuxtImg/IPX and ensures consistent URL formatting
 */

/**
 * Check if a URL has already been processed by a CDN or image processor
 * This prevents double processing when passing URLs to NuxtImg
 *
 * @param url - The URL to check
 * @returns true if the URL is already processed and should not be reprocessed
 */
export function isProcessedImageUrl(url: string | undefined | null): boolean {
	if (!url) return false;

	// External URLs (absolute) - already have their own CDN/processing
	if (url.startsWith('http://') || url.startsWith('https://')) return true;

	// Already processed by IPX/Nuxt Image
	if (url.startsWith('/_ipx/')) return true;

	return false;
}

/**
 * Convert a relative path to an absolute URL without duplicating the origin
 *
 * @param path - The path to convert (can be relative or absolute)
 * @param origin - The origin URL to prepend (e.g., 'https://example.com')
 * @returns The absolute URL
 */
export function toAbsoluteImageUrl(
	path: string | undefined | null,
	origin: string
): string {
	if (!path) return '';

	// Already absolute - don't add origin
	if (isProcessedImageUrl(path)) return path;

	// Ensure proper slash handling
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	return `${origin}${normalizedPath}`;
}

/**
 * Fallback image paths
 */
export const FALLBACK_IMAGES = {
	BROKER: '/images/fallbacks/agent-avatar.webp',
	COMPANY: '/images/fallbacks/company-avatar.webp'
} as const;

/**
 * Get the fallback broker image URL with absolute path
 *
 * @param origin - The origin URL
 * @returns The absolute fallback broker image URL
 */
export function getFallbackBrokerImage(origin: string): string {
	return toAbsoluteImageUrl(FALLBACK_IMAGES.BROKER, origin);
}

/**
 * Get the fallback company image URL with absolute path
 *
 * @param origin - The origin URL
 * @returns The absolute fallback company image URL
 */
export function getFallbackCompanyImage(origin: string): string {
	return toAbsoluteImageUrl(FALLBACK_IMAGES.COMPANY, origin);
}
